import { Component, ElementRef, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AnotacaoResponse,
  AulaResponse, AulaSlim,
  ModuloResponse, ModuloSlim,
  TrilhaModulosSlimResponse
} from 'app/shared/models/ei-models';
import { DefaultComponentsModule } from 'app/shared/modules/default-components.module';
import { AnotacaoService, AulaProgressService, AulaService, ModuloService, TrilhaService } from 'app/shared/services/edduca-imob';
import { fromEvent, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { GamificacaoHandler } from 'app/shared/components/gamificacao';

// ---- Tipos de visualização (com os campos extras opcionais)
type AulaSlimView = AulaSlim & { concluida?: boolean };
type ModuloSlimView = Omit<ModuloSlim, 'aulas'> & {
  aulas: AulaSlimView[];
  concluidoLocal?: boolean;
};

@Component({
  selector: 'app-aula-dashcards',
  imports: [DefaultComponentsModule],
  templateUrl: './aula-dashcards.component.html',
  styleUrl: './aula-dashcards.component.scss'
})
export class AulaDashcardsComponent {
  // ===== Dados principais =====
  aula!: AulaResponse;
  modulo!: ModuloResponse;
  arvore!: TrilhaModulosSlimResponse;
  modulosDaTrilha: ModuloSlimView[] = [];
  aulaId!: number;
  carregando = true;

  // ===== Notas =====
  anotacoes: AnotacaoResponse[] = [];
  anotacaoForm!: FormGroup;
  exibirAnotacoes = false;

  editandoId: number | null = null;        // controla modo edição
  editForm!: FormGroup;

  // ===== Sidebar (expansão) =====
  expandedModuloId: number | null = null;
  isExpanded = (m: ModuloSlimView) => this.expandedModuloId === m.moduloId;
  isModuloAtual = (m: ModuloSlimView) => m.moduloId === this.modulo?.id;
  toggleModulo(m: ModuloSlimView) {
    this.expandedModuloId = this.isExpanded(m) ? null : m.moduloId;
  }

  // ===== Player Panda / progresso =====
  @ViewChild('pandaIframe', { static: false }) pandaIframe?: ElementRef<HTMLIFrameElement>;
  private destroy$ = new Subject<void>();
  private lastSentSec = 0;      // throttle 5s
  private concludedSent = false;
  private listenersAttached = false; // garante que wirePandaListeners roda 1x

  // ===== Toast de auto-avanço =====
  nextToastVisible = false;
  nextToastSeconds = 10;
  private nextToastTimer: any = null;
  private nextToastInterval: any = null;
  private pendingNextAulaId: number | null = null;
  
  // ===== Gamificação =====
  private _gamificacaoHandler = inject(GamificacaoHandler);
  private _router = inject(Router);

  constructor(
    private route: ActivatedRoute,
    private aulaService: AulaService,
    private moduloService: ModuloService,
    private trilhaService: TrilhaService,
    private aulaProgress: AulaProgressService,
    private fb: FormBuilder,
    private anotacaoService: AnotacaoService
  ) { }

  // ========= Ciclo de vida =========
  ngOnInit(): void {
    this.aulaId = Number(this.route.snapshot.paramMap.get('id'));
    this.anotacaoForm = this.fb.group({
      texto: ['', [Validators.required, Validators.maxLength(300)]]
    });
    this.editForm = this.fb.group({
      texto: ['', [Validators.required, Validators.maxLength(300)]]
    });

    this.carregarAula(this.aulaId);
  }

  ngOnDestroy(): void {
    this.cancelNextToast();
    this.destroy$.next(); this.destroy$.complete();
  }

  // ========= Carregamento de dados =========
  carregarAula(aulaId: number): void {
    this.carregando = true;
    this.concludedSent = false;
    this.lastSentSec = 0;

    this.aulaService.obterPorId(aulaId).subscribe(aula => {
      this.aula = aula;
      this.carregarModuloEArvore(aula.moduloId);
      this.carregarAnotacoes(aula.id);

      // listeners do Panda apenas uma vez
      if (!this.listenersAttached) {
        setTimeout(() => { this.wirePandaListeners(); this.listenersAttached = true; }, 0);
      }
    });
  }

  private carregarModuloEArvore(moduloId: number): void {
    this.moduloService.obterPorId(moduloId).subscribe(modulo => {
      this.modulo = modulo;
      this.trilhaService.obterModulosEAulasSlim(modulo.trilhaId).subscribe(resp => {
        this.arvore = resp;

        // 1) mapear para tipos de view (sem 'as any')
        this.modulosDaTrilha = (resp.modulos || []).map(m => ({
          ...m,
          aulas: [...(m.aulas || [])].sort((a, b) => (a.ordemAula ?? 0) - (b.ordemAula ?? 0))
        })) as unknown as ModuloSlimView[];

        // 2) buscar progresso de TODAS as aulas dessa trilha
        const allAulaIds = this.modulosDaTrilha.flatMap(m => m.aulas.map(a => a.id));
        if (allAulaIds.length === 0) {
          this.modulosDaTrilha = this.modulosDaTrilha.map(m => ({ ...m, concluidoLocal: false }));
          this.expandedModuloId = this.modulo.id;
          this.carregando = false;
          return;
        }

        this.aulaProgress.getMany(allAulaIds).subscribe(progressList => {
          const concluidas = new Set(progressList.filter(p => p.concluida).map(p => p.aulaId));

          // 3) preencher flags de cada aula + calcular conclusão do módulo
          this.modulosDaTrilha = this.modulosDaTrilha.map(m => {
            const aulas = m.aulas.map(a => ({ ...a, concluida: concluidas.has(a.id) }));
            const concluidoLocal = aulas.length > 0 && aulas.every(x => !!x.concluida);
            return { ...m, aulas, concluidoLocal };
          });

          this.expandedModuloId = this.modulo.id;
          this.carregando = false;
        });
      });
    });
  }

  // ========= Navegação =========
  navegarParaAula(aula: AulaResponse | AulaSlimView): void {
    this.carregarAula(aula.id);
  }

  navegarParaAulaId(aulaId: number): void {
    this.carregarAula(aulaId);
  }

  /**
   * Próxima aula NA MESMA TRILHA, atravessando módulos se necessário.
   */
  private getProximaAulaNaTrilha(moduloId: number, aulaAtualId: number): AulaSlimView | null {
    if (!this.modulosDaTrilha?.length) return null;

    const modIndex = this.modulosDaTrilha.findIndex(m => m.moduloId === moduloId);
    if (modIndex < 0) return null;

    const modulo = this.modulosDaTrilha[modIndex];
    const aulas = modulo.aulas ?? [];
    const aulaIndex = aulas.findIndex(a => a.id === aulaAtualId);

    // 1) próxima dentro do módulo
    if (aulaIndex >= 0 && aulaIndex + 1 < aulas.length) {
      return aulas[aulaIndex + 1];
    }

    // 2) procurar nos próximos módulos
    for (let i = modIndex + 1; i < this.modulosDaTrilha.length; i++) {
      const nextMod = this.modulosDaTrilha[i];
      if (nextMod?.aulas?.length) {
        const sorted = [...nextMod.aulas].sort((a, b) => (a.ordemAula ?? 0) - (b.ordemAula ?? 0));
        return sorted[0];
      }
    }

    // 3) fim da trilha
    return null;
  }

  // ========= Anotações =========
  carregarAnotacoes(aulaId: number): void {
    this.anotacaoService.listarPorAula(aulaId).subscribe(list => {
      this.anotacoes = list;
    });
  }

  adicionarAnotacao(): void {
    if (this.anotacaoForm.invalid) return;
    const req = { aulaId: this.aula.id, texto: this.anotacaoForm.value.texto };
    this.anotacaoService.criar(req).subscribe(nova => {
      this.anotacoes = [...this.anotacoes, nova];
      this.anotacaoForm.reset();
    });
  }

  iniciarEdicao(nota: AnotacaoResponse) {
    this.editandoId = nota.id;
    this.editForm.setValue({ texto: nota.texto });
  }

  salvarEdicao() {
    if (this.editandoId == null || this.editForm.invalid) return;
    const id = this.editandoId;
    const req = { texto: this.editForm.value.texto };
    this.anotacaoService.atualizar(id, req).subscribe(notaAtualizada => {
      this.anotacoes = this.anotacoes.map(n => n.id === id ? notaAtualizada : n);
      this.editandoId = null;
      this.editForm.reset();
    });
  }

  cancelarEdicao() {
    this.editandoId = null;
    this.editForm.reset();
  }

  excluirAnotacao(id: number) {
    this.anotacaoService.remover(id).subscribe(() => {
      this.anotacoes = this.anotacoes.filter(n => n.id !== id);
    });
  }

  // ========= Panda: listeners + eventos =========
  private wirePandaListeners() {
    fromEvent<MessageEvent>(window, 'message').pipe(
      takeUntil(this.destroy$),
      filter(ev => !!ev?.data?.message && String(ev.data.message).startsWith('panda_')),
    ).subscribe(ev => this.onPandaEvent(ev.data));
  }

  private onPandaEvent(data: any) {
    switch (data.message) {
      case 'panda_timeupdate': {
        const sec = Math.floor(Number(data.currentTime) || 0);
        this.handleTimeUpdate(sec);
        break;
      }
      case 'panda_ended':
        this.handleEnded();
        break;
    }
  }

  private handleTimeUpdate(currentSec: number) {
    // throttle: envia no máx a cada 5s
    if (currentSec - this.lastSentSec < 5) return;
    this.lastSentSec = currentSec;

    this.aulaProgress.upsert(this.aula.id, currentSec, false)
      .pipe(takeUntil(this.destroy$))
      .subscribe(snap => {
        // marca só visualmente (sem interferir no autoplay)
        this.marcarAulaConcluidaLocal(this.modulo.id, this.aula.id, snap.concluida);

        // O backend marca a aula como concluída automaticamente ao passar de 95%.
        // Quando isso acontece dentro do tick, a gamificação vem populada AQUI
        // (e não mais no handleEnded). O backend garante exibição única via
        // `aulaFoiConcluidaAgora`, então é seguro processar nos dois handlers.
        if (snap.gamificacao) {
          this._gamificacaoHandler.processarGamificacao(snap.gamificacao);
        }
      });
  }

  private handleEnded() {
    if (this.concludedSent) return;
    this.concludedSent = true;

    const duracaoSeg = (this.aula as any)?.duracaoSegundos ?? 999999;
    this.aulaProgress.upsert(this.aula.id, duracaoSeg, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe(snap => {
        // marca aula e módulo como concluídos no UI
        this.marcarAulaConcluidaLocal(this.modulo.id, this.aula.id, true);
        
        // Processar gamificação (já vem unificada do backend quando é última aula do módulo)
        // Badges só vêm para usuários de plano 2+
        if (snap.gamificacao) {
          this._gamificacaoHandler.processarGamificacao(snap.gamificacao);
        }

        // próxima aula na trilha (pode estar no próximo módulo)
        const next = this.getProximaAulaNaTrilha(this.modulo.id, this.aula.id);
        if (next) this.showNextToast(next.id, 5);
      });
  }

  /**
   * Marca a aula concluída na árvore local e recalcula o estado concluído do módulo.
   */
  private marcarAulaConcluidaLocal(moduloId: number, aulaId: number, concluida: boolean) {
    const modulo = this.modulosDaTrilha.find(m => m.moduloId === moduloId);
    if (!modulo?.aulas?.length) return;

    const a = modulo.aulas.find(x => x.id === aulaId);
    if (a) a.concluida = concluida;

    modulo.concluidoLocal = modulo.aulas.length > 0 && modulo.aulas.every(x => !!x.concluida);
  }

  // ========= Toast de auto-avanço =========
  private showNextToast(nextAulaId: number, seconds = 5) {
    this.cancelNextToast();
    this.pendingNextAulaId = nextAulaId;
    this.nextToastSeconds = seconds;
    this.nextToastVisible = true;

    this.nextToastInterval = setInterval(() => {
      this.nextToastSeconds--;
      if (this.nextToastSeconds <= 0) {
        this.executeNextToast();
      }
    }, 1000);

    this.nextToastTimer = setTimeout(() => this.executeNextToast(), seconds * 1000);
  }

  private executeNextToast() {
    if (!this.pendingNextAulaId) return;
    const id = this.pendingNextAulaId;
    this.cancelNextToast();
    this.navegarParaAulaId(id);
  }

  cancelNextToast() {
    this.nextToastVisible = false;
    this.pendingNextAulaId = null;
    if (this.nextToastTimer) { clearTimeout(this.nextToastTimer); this.nextToastTimer = null; }
    if (this.nextToastInterval) { clearInterval(this.nextToastInterval); this.nextToastInterval = null; }
  }

  // ========= trackBys =========
  trackByModulo = (_: number, m: ModuloSlimView) => m.moduloId;
  trackByAula = (_: number, a: AulaSlimView) => a.id;

  // ========= Helpers =========
  getTotalAulas(): number {
    return this.modulosDaTrilha.reduce((acc, m) => acc + (m.aulas?.length || 0), 0);
  }

  voltarParaModulo(): void {
    if (this.modulo?.id) {
      this._router.navigate(['/modulo', this.modulo.id]);
    } else {
      this._router.navigate(['/academia-edduca/academia']);
    }
  }
}